import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { ChevronDown, Loader2, Settings2, Zap } from 'lucide-react';
import {
  CHANNEL_LABELS,
  MACRO_QUERY_KEY,
  recordMacroUse,
  resolveMacroTokens,
} from '@/lib/crmMacros';

// The one canned-response picker, shared by every admin reply box. It offers
// only the responses that suit the box it sits in — SMS snippets in SMS boxes,
// email templates in email boxes, plus anything tagged for both — and hands the
// resolved text back to the caller to place in its own field.
export default function MacroPicker({ channel, context, onInsert, onManage, className = '' }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const { data: macros = [], isLoading } = useQuery({
    queryKey: MACRO_QUERY_KEY,
    queryFn: () => base44.entities.CrmMacro.list('sort_order', 200),
  });

  const available = useMemo(
    () =>
      macros.filter(
        (m) => m.active !== false && (m.channel === 'any' || m.channel === channel)
      ),
    [macros, channel]
  );

  const grouped = useMemo(() => {
    const byCategory = new Map();
    for (const macro of available) {
      const key = macro.category || 'General';
      if (!byCategory.has(key)) byCategory.set(key, []);
      byCategory.get(key).push(macro);
    }
    return [...byCategory.entries()];
  }, [available]);

  // Close on a click outside, so the panel behaves like any other dropdown.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const choose = async (macro) => {
    setOpen(false);
    onInsert(resolveMacroTokens(macro.body, context), macro);
    await recordMacroUse(macro);
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 border border-white/10 hover:border-primary/50 bg-[#0d0d0f] text-white/50 hover:text-white text-[10px] font-bold tracking-widest px-3 py-2.5 transition-colors"
      >
        <Zap className="w-3.5 h-3.5 text-primary shrink-0" />
        <span className="flex-1 text-left">INSERT CANNED RESPONSE</span>
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-40 left-0 right-0 mt-1 border border-white/15 bg-[#111115] shadow-[0_12px_32px_rgba(0,0,0,0.6)] max-h-[320px] overflow-y-auto">
          {isLoading ? (
            <div className="p-4 flex justify-center">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
            </div>
          ) : available.length === 0 ? (
            <p className="px-4 py-4 text-white/30 text-[11px] leading-relaxed">
              No {CHANNEL_LABELS[channel] || ''} responses set up yet.
              {onManage ? ' Use “Manage responses” below to add one.' : ''}
            </p>
          ) : (
            grouped.map(([category, list]) => (
              <div key={category}>
                <p className="px-4 pt-3 pb-1.5 text-white/25 text-[9px] font-bold tracking-widest uppercase">
                  {category}
                </p>
                {list.map((macro) => (
                  <button
                    key={macro.id}
                    type="button"
                    onClick={() => choose(macro)}
                    className="w-full text-left px-4 py-2.5 hover:bg-white/5 transition-colors"
                  >
                    <span className="block text-white text-xs font-bold">{macro.name}</span>
                    {macro.subject && (
                      <span className="block text-white/30 text-[10px] truncate mt-0.5">
                        {macro.subject}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}

          {onManage && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onManage();
              }}
              className="sticky bottom-0 w-full flex items-center gap-2 border-t border-white/10 bg-[#111115] px-4 py-3 text-primary hover:bg-primary/5 text-[10px] font-bold tracking-widest transition-colors"
            >
              <Settings2 className="w-3.5 h-3.5" /> + MANAGE RESPONSES
            </button>
          )}
        </div>
      )}
    </div>
  );
}